import type { CameraState, ViewCamera } from '../types';
import { clampPitch } from '../geometry/projection';
import { turnTrackball, type TrackballSphere } from '../geometry/trackball';

/**
 * The ball a trackball drag turns: fixed over the canvas for a pointer, so a drag round its rim
 * rolls the view; under the previous point for the keys, so every step turns about the screen's
 * own axes however long a key is held. A turntable camera ignores it.
 */
export type DragBall =
  | { kind: 'fixed'; sphere: TrackballSphere }
  | { kind: 'underAnchor'; radius: number };

/**
 * Drag state kept beside the camera rather than inside it, so the camera stays a plain
 * description of a viewpoint that a story or a saved view can set directly.
 */
export type OrbitState<C extends ViewCamera = CameraState> = {
  camera: C;
  /**
   * The press, its anchor, and whether it has yet moved.
   *
   * `hasTurned` is the distinction between a press and a rotation, and they want different
   * answers. A press is how the chart is focused and how a click begins; a rotation is the
   * gesture the reduced-resolution buffer and the suppressed hover were written for. Keyed on the
   * press alone, both fired on `pointerdown` - so pressing and holding softened the surface and
   * cleared the tooltip for a gesture that had not happened.
   */
  drag: { x: number; y: number; ball: DragBall; hasTurned: boolean } | null;
};

export type OrbitGesture<C extends ViewCamera = CameraState> =
  | { type: 'dragStart'; x: number; y: number; ball: DragBall }
  | { type: 'dragMove'; x: number; y: number }
  | { type: 'dragEnd' }
  | { type: 'wheel'; delta: number }
  | { type: 'setCamera'; camera: C };

/** Radians of rotation per pixel dragged. Tuned so a drag across a mid-sized chart turns
 *  it roughly three quarters of the way round - enough to explore, not enough to lose
 *  which way is which. */
export const RADIANS_PER_PIXEL = 0.009;

/**
 * Radians per arrow-key press. A step big enough to feel, small enough to aim.
 *
 * Here rather than beside each keyboard handler, with {@link RADIANS_PER_PIXEL} and
 * {@link ZOOM_KEY_DELTA}. Both handlers convert their step back into pixels for the same gesture
 * this reducer applies, so a copy of any of the three retunes one input path and silently leaves
 * the other where it was - which is the drift routing both through one gesture was meant to
 * prevent.
 */
export const KEYBOARD_STEP = 0.12;

/** Wheel delta a zoom key stands in for, so the two paths zoom by the same amount. */
export const ZOOM_KEY_DELTA = 120;

const ZOOM_PER_WHEEL_STEP = 0.0015;
/**
 * Exported so a test can sweep it rather than restate its ends.
 *
 * The reason is specific: the chart does not render at zoom 1. `fitZoomToViewport` replaces it on
 * mount and on every resize, and a reader can pinch to either end of this - so anything sized in
 * pixels rather than in the projection has to be checked here and not at the default.
 */
export const ZOOM_RANGE: [number, number] = [0.35, 4];

export const clampZoom = (zoom: number): number =>
  Math.max(ZOOM_RANGE[0], Math.min(ZOOM_RANGE[1], zoom));

const isSameOrientation = (left: ViewCamera, right: ViewCamera): boolean => {
  if ('orientation' in left && 'orientation' in right) {
    return left.orientation.every(
      (component, index) => component === right.orientation[index]
    );
  }
  if ('orientation' in left || 'orientation' in right) return false;
  return left.yaw === right.yaw && left.pitch === right.pitch;
};

/** Every field, rather than a reference: two cameras describing one viewpoint are one viewpoint. */
export const isSameCamera = (left: ViewCamera, right: ViewCamera): boolean =>
  isSameOrientation(left, right) &&
  left.zoom === right.zoom &&
  left.perspective === right.perspective &&
  left.heightScale === right.heightScale;

/**
 * `deltaY` in pixels, whatever the browser reported it in.
 *
 * `deltaMode` says whether the number means pixels, lines or pages, and Firefox reports lines for
 * a classic wheel mouse - about 3 per notch against Chromium's 100. Read raw, that made a notch
 * move the zoom by 0.45% instead of 15%, which reads as broken rather than slow. Normalised here
 * so the reducer's step stays one unit rather than three.
 */
const LINE_HEIGHT = 16;
const PAGE_HEIGHT = 400;

export const toPixelDelta = ({ deltaY, deltaMode }: WheelEvent): number => {
  if (deltaMode === 1) return deltaY * LINE_HEIGHT;
  if (deltaMode === 2) return deltaY * PAGE_HEIGHT;
  return deltaY;
};

/**
 * Whether the browser owns this wheel gesture rather than the chart.
 *
 * Ctrl or Cmd plus wheel is page zoom. Swallowing it means a reader cannot enlarge the page while
 * the pointer is over the chart - which, on an element sized to fill its pane, is most of the
 * time and is the thing they were most likely trying to enlarge.
 */
export const isBrowserZoomGesture = ({
  ctrlKey,
  metaKey,
}: WheelEvent): boolean => Boolean(ctrlKey || metaKey);

const toSphere = (
  ball: DragBall,
  anchor: { x: number; y: number }
): TrackballSphere =>
  ball.kind === 'fixed'
    ? ball.sphere
    : { x: anchor.x, y: anchor.y, radius: ball.radius };

const turnCamera = <C extends ViewCamera>(
  camera: C,
  drag: NonNullable<OrbitState<C>['drag']>,
  to: { x: number; y: number }
): C => {
  if ('orientation' in camera) {
    return {
      ...camera,
      orientation: turnTrackball(
        camera.orientation,
        toSphere(drag.ball, drag),
        drag,
        to
      ),
    };
  }
  /* Horizontal drag turns the plot, vertical drag raises the eye. Pitch is inverted so
     dragging down tips the far edge up, which is how grabbing the surface itself would
     feel rather than how moving a camera would. */
  return {
    ...camera,
    yaw: camera.yaw + (to.x - drag.x) * RADIANS_PER_PIXEL,
    pitch: clampPitch(camera.pitch + (to.y - drag.y) * RADIANS_PER_PIXEL),
  };
};

export const orbitReducer = <C extends ViewCamera>(
  state: OrbitState<C>,
  gesture: OrbitGesture<C>
): OrbitState<C> => {
  if (gesture.type === 'setCamera') {
    /* The same viewpoint expressed as a different object leaves the state alone, so a caller that
       builds its camera inline does not repaint on every render. */
    if (isSameCamera(state.camera, gesture.camera)) return state;
    return { ...state, camera: gesture.camera };
  }

  if (gesture.type === 'dragStart') {
    return {
      ...state,
      drag: {
        x: gesture.x,
        y: gesture.y,
        ball: gesture.ball,
        hasTurned: false,
      },
    };
  }

  if (gesture.type === 'dragEnd') {
    /* A no-op when nothing is open, matching the other three branches. Both `pointercancel` and
       `bindCanvas(null)` end a drag unconditionally, and a fresh object read as a change to
       consumers that bail on identity - one React render and one frame absorbed by the gate for a
       state that had not moved. */
    if (state.drag === null) return state;
    return { ...state, drag: null };
  }

  if (gesture.type === 'wheel') {
    const zoom = clampZoom(
      state.camera.zoom * (1 - gesture.delta * ZOOM_PER_WHEEL_STEP)
    );
    /* At either end of the range the clamp hands back the zoom already set, and a new object reads
       as a change to both consumers - they bail on identity. Scrolling into the limit and holding
       is an ordinary thing to do, and it cost a repaint per frame for a picture that had stopped
       moving. */
    if (zoom === state.camera.zoom) return state;

    return { ...state, camera: { ...state.camera, zoom } };
  }

  if (!state.drag) return state;

  /* A move delivered at the anchor turns nothing, and a fresh object would read as a change to
     consumers that bail on identity - same reason as the clamped wheel above. Pointer streams
     repeat coordinates more often than they look like they should. */
  if (gesture.x === state.drag.x && gesture.y === state.drag.y) return state;

  const camera = turnCamera(state.camera, state.drag, gesture);

  /* Bailing on the result as well as on the gesture, which is what the wheel branch above already
     does. A move the pitch clamp absorbs travels a real distance and turns nothing, so the guard
     above cannot see it - and the keyboard reaches it exactly rather than occasionally: `rotate`
     anchors at zero and dispatches the step as the move, so holding ArrowDown at the limit is one
     full rasterise per key repeat. The drag anchor still advances, or the gesture would jump when
     the pointer came back off the limit. */
  /* A move the clamp absorbs still counts as turning: the reader is dragging, and the plot will
     start moving again the moment they come off the limit. */
  const drag = {
    x: gesture.x,
    y: gesture.y,
    ball: state.drag.ball,
    hasTurned: true,
  };

  if (isSameCamera(state.camera, camera)) return { ...state, drag };

  return { camera, drag };
};
